import { callAPI } from '@/store/socket';
import {
  THEMES, DEFAULT_THEME, DEFAULT_THEME_BY_MODE, applyTheme, currentTheme, isLight, lastThemeIdOfMode,
} from '@/ui/tokens/themes';
import {
  NEW_YEAR_THEME_ID, isDevforthUser, isNewYearSeason, previousThemeId,
} from '@/components/newYear/newYearSeason';

async function callThemeAPI(method, params) {
  const result = await callAPI({ method, params });
  if (result?.error) throw new Error(`${method} failed: ${result.error}`);
  return result;
}

export default {
  namespaced: true,
  state: () => ({
    customThemes: [],
  }),
  getters: {
    newYearSeason: (state, getters, rootState) => isNewYearSeason(rootState.system.nowUTC.toDate()),
    themes: (state, getters, rootState) => [
      ...THEMES.filter(theme => theme.id !== NEW_YEAR_THEME_ID
        || (getters.newYearSeason && (!rootState.user.user || isDevforthUser(rootState.user.user)))),
      ...state.customThemes,
    ],
    findTheme: (_, getters) => id => getters.themes.find(theme => theme.id === id),
  },
  mutations: {
    setCustomThemes(state, themes) {
      state.customThemes = themes;
    },
    upsertCustomTheme(state, theme) {
      const index = state.customThemes.findIndex(t => t.id === theme.id);
      if (index === -1) state.customThemes.push(theme);
      else state.customThemes.splice(index, 1, theme);
    },
    removeCustomTheme(state, id) {
      state.customThemes = state.customThemes.filter(theme => theme.id !== id);
    },
  },
  actions: {
    async actionLoadThemes({ commit, dispatch, getters }) {
      const { themes, selectedThemeId } = await callAPI({
        method: 'getUiThemesAndSubscribe',
        params: {},
      });
      commit('setCustomThemes', themes);
      // never picked on the server yet: the theme chosen on this device stays
      if (!selectedThemeId) return;
      if (selectedThemeId === NEW_YEAR_THEME_ID && !getters.newYearSeason) {
        await dispatch('actionLeaveNewYearTheme');
        return;
      }
      // a shared theme its author deleted falls back to the default one
      applyTheme(getters.findTheme(selectedThemeId) || getters.findTheme(DEFAULT_THEME));
    },
    async actionSelectTheme({ getters }, id) {
      applyTheme(getters.findTheme(id));
      await callAPI({
        method: 'selectUiTheme',
        params: { themeId: id },
        persisted: true,
      });
    },
    actionLeaveNewYearTheme({ dispatch, getters }) {
      const previousId = previousThemeId();
      return dispatch('actionSelectTheme', getters.findTheme(previousId) ? previousId : DEFAULT_THEME);
    },
    actionToggleMode({ dispatch, getters }) {
      const otherMode = isLight.value ? 'dark' : 'light';
      const id = lastThemeIdOfMode(otherMode);
      // the last theme of that mode may have been deleted since
      return dispatch('actionSelectTheme', getters.findTheme(id) ? id : DEFAULT_THEME_BY_MODE[otherMode]);
    },
    async actionCreateTheme({ commit, dispatch }, theme) {
      const created = await callThemeAPI('createUiTheme', theme);
      commit('upsertCustomTheme', created);
      await dispatch('actionSelectTheme', created.id);
    },
    async actionUpdateTheme({ dispatch }, theme) {
      const updated = await callThemeAPI('updateUiTheme', theme);
      dispatch('applyThemeSaved', updated);
    },
    async actionDeleteTheme({ dispatch }, id) {
      await callThemeAPI('deleteUiTheme', { id });
      dispatch('applyThemeDeleted', { id });
    },

    applyThemeSaved({ commit, dispatch, rootState }, theme) {
      const visible = theme.userId === rootState.user.user.id
        || theme.sharedWorkspaceId === rootState.user.workspace.id;
      if (!visible) {
        dispatch('applyThemeDeleted', theme);
        return;
      }
      commit('upsertCustomTheme', theme);
      if (currentTheme.value === theme.id) applyTheme(theme);
    },
    applyThemeDeleted({ commit, getters }, { id }) {
      commit('removeCustomTheme', id);
      if (currentTheme.value === id) applyTheme(getters.findTheme(DEFAULT_THEME));
    },
    applyThemeSelected({ getters }, { themeId }) {
      applyTheme(getters.findTheme(themeId) || getters.findTheme(DEFAULT_THEME));
    },
  },
};
